import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { trackSignupStartConversion } from '../utils/adsConversion'
import { isValidBtw } from '../utils/btw'
import {
  Mail, Lock, User, Building, Phone, Calendar, Video, Briefcase,
  CheckCircle, ArrowLeft, UserPlus, Eye, EyeOff, AlertCircle, Users, ChevronLeft
} from 'lucide-react'

const ACCENT = {
  creator: { ring: 'focus:ring-[#7c3aed] focus:border-[#7c3aed]', text: 'text-[#7c3aed]', bg: 'bg-[#7c3aed]', hover: 'hover:bg-[#6d28d9]', border: 'border-[#7c3aed]', bgLight: 'bg-purple-50' },
  bedrijf: { ring: 'focus:ring-[#3EB489] focus:border-[#3EB489]', text: 'text-[#2A7E5F]', bg: 'bg-[#3EB489]', hover: 'hover:bg-[#359E78]', border: 'border-[#3EB489]', bgLight: 'bg-[#FAFAF7]' },
  agency:  { ring: 'focus:ring-[#10B981] focus:border-[#10B981]', text: 'text-[#10B981]', bg: 'bg-[#10B981]', hover: 'hover:bg-[#059669]', border: 'border-[#10B981]', bgLight: 'bg-emerald-50' },
}

const DarkRegisterPage = () => {
  const [step, setStep] = useState(1)
  const [selectedRole, setSelectedRole] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [verificationEmail, setVerificationEmail] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [searchParams] = useSearchParams()
  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '',
    firstName: '', lastName: '', dateOfBirth: '', phoneNumber: '', bio: '',
    btwStatus: '', kvkNummerCreator: '',
    socialMedia: { instagram: '', tiktok: '', youtube: '' },
    companyName: '', contactPerson: '', companySize: '', industry: '', website: '',
    kvkNummer: '', btwNummer: '',
    agencyName: '', agencySize: '', agencySpecializations: []
  })
  const [errors, setErrors] = useState({})
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [agreeTerms, setAgreeTerms] = useState(false)

  const { register, verifyLogin, resendVerificationCode } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    const error = searchParams.get('error')
    const message = searchParams.get('message')
    if (error === 'role_mismatch' && message) setErrors({ general: decodeURIComponent(message) })
    else if (error === 'oauth_failed') setErrors({ general: 'Google registratie mislukt. Probeer het opnieuw.' })
    else if (error === 'session_failed') setErrors({ general: 'Sessie fout. Probeer het opnieuw.' })
  }, [searchParams])

  const handleRoleSelect = (role) => { trackSignupStartConversion(role); setSelectedRole(role); setStep(2) }

  // ?role=business|bedrijf|company|creator|agency|bureau → rol vooraf kiezen en direct naar stap 2
  useEffect(() => {
    const raw = (searchParams.get('role') || '').toLowerCase()
    const map = { business: 'bedrijf', bedrijf: 'bedrijf', company: 'bedrijf', creator: 'creator', agency: 'agency', bureau: 'agency' }
    const role = map[raw]
    if (role && !selectedRole) handleRoleSelect(role)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name.includes('.')) {
      const [parent, child] = name.split('.')
      setFormData(prev => ({ ...prev, [parent]: { ...prev[parent], [child]: value } }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.email) newErrors.email = 'E-mailadres is verplicht'
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Ongeldig e-mailadres'
    if (!formData.password) newErrors.password = 'Wachtwoord is verplicht'
    else if (formData.password.length < 6) newErrors.password = 'Wachtwoord moet minimaal 6 tekens bevatten'
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Bevestig je wachtwoord'
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Wachtwoorden komen niet overeen'

    if (selectedRole === 'creator') {
      if (!formData.firstName) newErrors.firstName = 'Voornaam is verplicht'
      if (!formData.lastName) newErrors.lastName = 'Achternaam is verplicht'
      if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Geboortedatum is verplicht'
      if (!formData.phoneNumber) newErrors.phoneNumber = 'Telefoonnummer is verplicht'
      if (!formData.bio) newErrors.bio = 'Bio is verplicht'
      if (!formData.btwStatus) newErrors.btwStatus = 'Selecteer je fiscale status'
      if (formData.btwStatus === 'btw_plichtig' || formData.btwStatus === 'kor') {
        if (!formData.kvkNummerCreator) newErrors.kvkNummerCreator = 'KVK-nummer is verplicht'
        else if (!/^\d{8}$/.test(formData.kvkNummerCreator)) newErrors.kvkNummerCreator = 'KVK-nummer moet 8 cijfers bevatten'
      }
      if (formData.btwStatus === 'btw_plichtig') {
        if (!formData.btwNummer) newErrors.btwNummer = 'BTW-nummer is verplicht'
        else if (!isValidBtw(formData.btwNummer)) newErrors.btwNummer = 'BTW-nummer moet in formaat NL123456789B01 zijn'
      }
    }
    if (selectedRole === 'bedrijf') {
      if (!formData.companyName) newErrors.companyName = 'Bedrijfsnaam is verplicht'
      if (!formData.contactPerson) newErrors.contactPerson = 'Contactpersoon is verplicht'
      if (!formData.companySize) newErrors.companySize = 'Bedrijfsgrootte is verplicht'
      if (!formData.industry) newErrors.industry = 'Branche is verplicht'
    }
    if (selectedRole === 'agency') {
      if (!formData.agencyName) newErrors.agencyName = 'Agencynaam is verplicht'
      if (!formData.contactPerson) newErrors.contactPerson = 'Contactpersoon is verplicht'
      if (!formData.agencySize) newErrors.agencySize = 'Agency grootte is verplicht'
      if (!formData.industry) newErrors.industry = 'Branche is verplicht'
      if (!formData.kvkNummer) newErrors.kvkNummer = 'KVK-nummer is verplicht'
      else if (!/^\d{8}$/.test(formData.kvkNummer)) newErrors.kvkNummer = 'KVK-nummer moet 8 cijfers bevatten'
      if (!formData.btwNummer) newErrors.btwNummer = 'BTW-nummer is verplicht'
      else if (!isValidBtw(formData.btwNummer)) newErrors.btwNummer = 'BTW-nummer moet in formaat NL123456789B01 zijn'
    }
    return newErrors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const formErrors = validateForm()
    if (Object.keys(formErrors).length > 0) { setErrors(formErrors); return }
    setLoading(true); setErrors({})
    try {
      const registrationData = {
        ...formData,
        role: selectedRole,
        agreeTerms,
        ...(selectedRole === 'creator' ? {
          kvkNummer: formData.kvkNummerCreator || undefined,
          btwNummer: formData.btwNummer || undefined,
          btwStatus: formData.btwStatus,
          agreeSelfBilling: agreeTerms,
        } : {}),
        ...(selectedRole === 'agency' ? {
          agencyName: formData.agencyName,
          agencySize: formData.agencySize,
          specializations: formData.agencySpecializations,
        } : {})
      }
      const result = await register(registrationData)
      if (result && result.requiresVerification) {
        setVerificationEmail(result.email)
        setStep(3)
        setLoading(false)
        return
      }
      if (result.success) {
        // Quick-brief flow: redirect naar opdracht-plaatsen ipv dashboard
        const intent = searchParams.get('intent')
        navigate(intent === 'brief' ? '/projects/create?fromQuickBrief=1' : '/dashboard')
      } else setErrors({ general: result.message || 'Er is een fout opgetreden bij het aanmelden' })
    } catch (error) {
      if (error.response?.data?.message) setErrors({ general: error.response.data.message })
      else if (error.response?.data?.messages) setErrors({ general: error.response.data.messages.join(', ') })
      else setErrors({ general: 'Er is een fout opgetreden. Probeer het opnieuw.' })
    } finally {
      setLoading(false)
    }
  }

  const handleVerificationSubmit = async (e) => {
    e.preventDefault()
    if (!verificationCode || verificationCode.length !== 6) {
      setErrors({ verification: 'Voer een geldige 6-cijferige code in' }); return
    }
    setLoading(true); setErrors({})
    try {
      await verifyLogin(verificationEmail, verificationCode, 'registration')
      const intent = searchParams.get('intent')
      navigate(intent === 'brief' ? '/projects/create?fromQuickBrief=1' : '/dashboard')
    } catch (error) {
      if (error.response?.data?.message) setErrors({ verification: error.response.data.message })
      else setErrors({ verification: 'Ongeldige verificatiecode. Probeer het opnieuw.' })
    } finally {
      setLoading(false)
    }
  }

  const handleResendCode = async () => {
    setResendLoading(true); setErrors({})
    try {
      await resendVerificationCode(verificationEmail, 'registration')
      setErrors({ verification: 'Een nieuwe code is verstuurd naar je email' })
    } catch (error) {
      setErrors({ verification: 'Kon geen nieuwe code versturen. Probeer het opnieuw.' })
    } finally {
      setResendLoading(false)
    }
  }

  const accent = ACCENT[selectedRole] || ACCENT.bedrijf
  const isPositiveVerification = errors.verification && errors.verification.includes('verstuurd')

  // Step 2 / 3 share the wider main; step 1 (role selection) too — all wrapped in SplitLayout
  return (
    <SplitLayout>
      {step === 1 && <RoleSelection onSelect={handleRoleSelect} />}

      {step === 2 && (
        <FormView
          selectedRole={selectedRole}
          accent={accent}
          formData={formData}
          errors={errors}
          loading={loading}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          showConfirmPassword={showConfirmPassword}
          setShowConfirmPassword={setShowConfirmPassword}
          agreeTerms={agreeTerms}
          setAgreeTerms={setAgreeTerms}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          onBackToRole={() => setStep(1)}
        />
      )}

      {step === 3 && (
        <VerificationView
          verificationEmail={verificationEmail}
          verificationCode={verificationCode}
          setVerificationCode={setVerificationCode}
          errors={errors}
          setErrors={setErrors}
          loading={loading}
          resendLoading={resendLoading}
          isPositiveVerification={isPositiveVerification}
          onSubmit={handleVerificationSubmit}
          onResend={handleResendCode}
          onBack={() => { setStep(2); setVerificationCode(''); setErrors({}) }}
        />
      )}
    </SplitLayout>
  )
}

// ====== SplitLayout ======

const SplitLayout = ({ children }) => (
  <div className="font-['Geist',ui-sans-serif,system-ui] min-h-screen flex bg-white text-[#0A0F0D] antialiased">
    <style>{`
      @keyframes ugc-pulse { 0%{transform:scale(1);opacity:.5} 100%{transform:scale(2.2);opacity:0} }
      @keyframes ugc-scrollLeft { from{transform:translateX(0)} to{transform:translateX(-50%)} }
      @keyframes ugc-scrollRight { from{transform:translateX(-50%)} to{transform:translateX(0)} }
      .ugc-pulse-dot::before { content:""; position:absolute; inset:0; border-radius:9999px; background:#A7F3D0; opacity:.4; animation: ugc-pulse 2.2s infinite; }
      .ugc-marquee-mask { -webkit-mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent); mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent); }
      .ugc-track-1 { animation: ugc-scrollLeft 38s linear infinite; }
      .ugc-track-2 { animation: ugc-scrollRight 38s linear infinite; }
      .ugc-trustpilot-star::before { content:""; width:18px; height:18px; background:#00B67A; border-radius:3px; display:inline-block; background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23fff'><polygon points='12 2 15 9 22 9.5 17 14.5 18.5 22 12 18 5.5 22 7 14.5 2 9.5 9 9'/></svg>"); background-size:contain; background-repeat:no-repeat; background-position:center; }
    `}</style>

    <aside className="hidden lg:flex sticky top-0 self-start h-screen flex-[0.95] flex-col justify-between overflow-hidden text-white rounded-r-[48px] pt-12"
           style={{ background: 'linear-gradient(160deg,#047857 0%,#059669 45%,#065F46 100%)' }}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[300px] -right-[300px] w-[900px] h-[900px] rounded-full blur-[40px]"
             style={{ background: 'radial-gradient(circle, rgba(167,243,208,.35), transparent 60%)' }} />
        <div className="absolute -bottom-[200px] -left-[200px] w-[700px] h-[700px] rounded-full blur-[60px]"
             style={{ background: 'radial-gradient(circle, rgba(139,92,246,.25), transparent 60%)' }} />
        <div className="absolute -top-[120px] -right-[180px] w-[520px] h-[520px] grid place-items-center font-extrabold leading-[0.9] -rotate-[22deg] select-none pointer-events-none"
             style={{ fontSize: 520, color: 'rgba(255,255,255,.08)' }} aria-hidden="true">U</div>
        <div className="absolute inset-0 mix-blend-soft-light opacity-[0.08] pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '5px 5px' }} />
      </div>

      <Link to="/" className="absolute top-8 left-12 z-10 flex items-center gap-2.5 font-bold tracking-tight text-[19px] text-white">
        <span className="w-8 h-8 rounded-[10px] grid place-items-center font-extrabold text-sm border border-white/20 backdrop-blur-md"
              style={{ background: 'rgba(255,255,255,.15)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.2)' }}>U</span>
        UGC<b className="text-[#A7F3D0] font-extrabold">4you</b>
      </Link>

      <div className="relative z-[2] flex-1 flex flex-col items-center justify-center gap-10 px-14 pt-20 pb-8">
        <div className="inline-flex items-center gap-2.5 pl-1.5 pr-3.5 py-1.5 rounded-full text-[13px] font-medium text-white border border-white/20 backdrop-blur-md"
             style={{ background: 'rgba(255,255,255,.1)' }}>
          <span className="relative w-[18px] h-[18px] rounded-full grid place-items-center ugc-pulse-dot" style={{ background: 'rgba(255,255,255,.2)' }}>
            <span className="w-[7px] h-[7px] rounded-full bg-[#A7F3D0]" />
          </span>
          1000+ creators &amp; merken samen
        </div>

        <h1 className="text-center font-semibold leading-[1.05] tracking-[-0.032em] max-w-[540px]"
            style={{ fontSize: 'clamp(34px,3.2vw,48px)' }}>
          Start je <span className="text-[#A7F3D0] font-bold">UGC reis</span> en groei samen met <span className="text-[#A7F3D0] font-bold">top merken</span>.
        </h1>

        <div className="flex items-center gap-[18px] py-3.5 px-5 rounded-2xl border border-white/15 backdrop-blur-md"
             style={{ background: 'rgba(255,255,255,.08)' }}>
          <span className="ugc-trustpilot-star inline-flex items-center gap-1.5 font-bold text-white text-[13.5px]">Trustpilot</span>
          <div className="w-px h-9 bg-white/20" />
          <div className="flex flex-col gap-0.5">
            <div className="text-[15px] font-semibold text-white flex items-baseline gap-1">
              4.6 <em className="not-italic text-white/50 font-normal text-[13px]">/ 5.0</em>
            </div>
            <div className="text-[#FBBF24] text-[13px] tracking-[1.5px]">★★★★★</div>
          </div>
        </div>
      </div>

      <div className="relative z-[2] flex flex-col gap-3.5 pb-10">
        <div className="overflow-hidden ugc-marquee-mask">
          <div className="ugc-track-1 flex gap-12 w-max items-center">
            {[
              ['Magnum','bold'],['DYSON','thin'],['Starbucks','serif'],["L'Oréal",'bold'],
              ['ABN AMRO','mono'],['COLGATE','thin'],['Sophia Mae','serif'],["Kellogg's",'bold'],
              ['Magnum','bold'],['DYSON','thin'],['Starbucks','serif'],["L'Oréal",'bold'],
              ['ABN AMRO','mono'],['COLGATE','thin'],['Sophia Mae','serif'],["Kellogg's",'bold'],
            ].map(([name, kind], i) => <Brand key={i} name={name} kind={kind} />)}
          </div>
        </div>
        <div className="overflow-hidden ugc-marquee-mask">
          <div className="ugc-track-2 flex gap-12 w-max items-center">
            {[
              ['HEMA','mono'],['Albert Heijn','serif'],['Bol.com','bold'],['RITUALS','thin'],
              ['Coolblue','serif'],['Jumbo','bold'],['DECATHLON','thin'],['KPN','mono'],
              ['HEMA','mono'],['Albert Heijn','serif'],['Bol.com','bold'],['RITUALS','thin'],
              ['Coolblue','serif'],['Jumbo','bold'],['DECATHLON','thin'],['KPN','mono'],
            ].map(([name, kind], i) => <Brand key={i} name={name} kind={kind} />)}
          </div>
        </div>
      </div>
    </aside>

    <main className="flex-1 min-w-0 w-full flex flex-col min-h-screen bg-white relative">
      <div className="px-5 py-4 sm:px-8 sm:py-6 flex justify-end">
        <Link to="/" className="inline-flex items-center gap-2 text-[#64748B] text-sm font-medium px-3.5 py-2 rounded-lg hover:bg-[#F1F3F5] hover:text-[#0A0F0D] transition">
          <ChevronLeft className="w-3.5 h-3.5" />
          Terug naar home
        </Link>
      </div>

      <div className="flex-1 min-w-0 w-full flex flex-col items-center px-4 sm:px-8 pb-10">
        {children}
      </div>

      <div className="px-5 sm:px-8 py-4 sm:py-5 border-t border-[#F1F3F5] flex flex-col sm:flex-row gap-2 justify-between items-center text-xs text-[#64748B]">
        <span className="py-1">© 2026 UGC4you</span>
        <div className="flex flex-wrap justify-center gap-x-5 gap-y-1">
          <Link to="/privacy-policy" className="inline-flex items-center py-2 hover:text-[#0A0F0D]">Privacy</Link>
          <Link to="/algemene-voorwaarden" className="inline-flex items-center py-2 hover:text-[#0A0F0D]">Voorwaarden</Link>
          <a href="mailto:support@ugc4you.nl" className="inline-flex items-center py-2 hover:text-[#0A0F0D]">Support</a>
        </div>
      </div>
    </main>
  </div>
)

const Brand = ({ name, kind }) => {
  const cls = {
    bold: 'font-extrabold tracking-[-0.04em] text-[22px]',
    thin: 'font-light tracking-[0.04em] uppercase text-[18px]',
    serif: "font-normal italic text-[22px] font-['Georgia',serif]",
    mono: "font-semibold tracking-[-0.04em] text-[20px] font-['ui-monospace',monospace]",
  }[kind] || 'font-extrabold text-[22px]'
  return <span className={`flex-shrink-0 whitespace-nowrap text-white/40 ${cls}`}>{name}</span>
}

// ====== Step 1: Role selection ======

const RoleSelection = ({ onSelect }) => (
  <div className="w-full max-w-3xl pt-2 lg:pt-8">
    <div className="lg:hidden flex items-center justify-center gap-2.5 font-bold tracking-tight text-[22px] mb-8">
      <span className="w-8 h-8 rounded-[10px] grid place-items-center font-extrabold text-sm text-white bg-[#10B981] shadow-[0_4px_10px_-3px_rgba(16,185,129,0.5)]">U</span>
      UGC<b className="text-[#059669] font-extrabold">4you</b>
    </div>

    <div className="text-center mb-8">
      <h1 className="text-[32px] md:text-[40px] font-bold tracking-[-0.03em] leading-[1.1] text-[#0A0F0D]">
        Wat beschrijft jou het beste?
      </h1>
      <p className="mt-2 text-[#64748B] text-[15px] leading-relaxed">
        Kies je rol om verder te gaan met registreren
      </p>
    </div>

    <div className="grid md:grid-cols-3 gap-4">
      <RoleCard
        onClick={() => onSelect('creator')}
        icon={<Video className="w-6 h-6 text-white" />}
        iconBg="bg-gradient-to-br from-[#7c3aed] to-pink-500"
        title="Ik ben een Creator"
        desc="Ik maak content en wil geld verdienen met mijn creativiteit"
        bullets={['Reageer op opdrachten', 'Bouw je portfolio uit', 'Verdien geld met je passie']}
        accentText="text-[#7c3aed]"
        hoverBorder="hover:border-[#7c3aed]"
      />
      <RoleCard
        onClick={() => onSelect('bedrijf')}
        icon={<Briefcase className="w-6 h-6 text-white" />}
        iconBg="bg-gradient-to-br from-[#3EB489] to-[#2A7E5F]"
        title="Ik ben een Bedrijf"
        desc="Ik zoek creators die authentieke content maken voor mijn merk"
        bullets={['Plaats content opdrachten', 'Vind de perfecte creator', 'Krijg authentieke UGC']}
        accentText="text-[#2A7E5F]"
        hoverBorder="hover:border-[#3EB489]"
      />
      <RoleCard
        onClick={() => onSelect('agency')}
        icon={<Users className="w-6 h-6 text-white" />}
        iconBg="bg-gradient-to-br from-[#10B981] to-[#3EB489]"
        title="Ik ben een Agency"
        desc="Ik beheer meerdere merken en zoek creators voor mijn klanten"
        bullets={['Beheer meerdere merken', 'Vind creators voor klanten', 'Centraal dashboard']}
        accentText="text-[#10B981]"
        hoverBorder="hover:border-[#10B981]"
      />
    </div>

    <div className="mt-8 text-center text-sm text-[#64748B]">
      Heb je al een account?{' '}
      <Link to="/login" className="font-semibold text-[#047857] hover:text-[#065F46] hover:underline">
        Log hier in
      </Link>
    </div>
  </div>
)

const RoleCard = ({ onClick, icon, iconBg, title, desc, bullets, accentText, hoverBorder }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left bg-white border-2 border-[#E5E7EB] rounded-2xl p-5 md:p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${hoverBorder}`}
  >
    <div className={`w-12 h-12 rounded-xl ${iconBg} flex items-center justify-center mb-4 shadow-sm`}>
      {icon}
    </div>
    <h3 className="text-lg font-bold text-[#0A0F0D] mb-1.5">{title}</h3>
    <p className="text-sm text-[#64748B] mb-4 leading-relaxed">{desc}</p>
    <ul className="space-y-2">
      {bullets.map((b, i) => (
        <li key={i} className="flex items-center text-sm text-[#1F2937]">
          <CheckCircle className={`w-4 h-4 mr-2 flex-shrink-0 ${accentText}`} />
          {b}
        </li>
      ))}
    </ul>
  </button>
)

// ====== Step 2: Form ======

const FormView = ({
  selectedRole, accent, formData, errors, loading,
  showPassword, setShowPassword, showConfirmPassword, setShowConfirmPassword,
  agreeTerms, setAgreeTerms, handleChange, handleSubmit, onBackToRole,
}) => {
  const title = selectedRole === 'creator' ? 'Maak je Creator profiel' :
                selectedRole === 'agency' ? 'Maak je Agency profiel' :
                'Maak je Bedrijf profiel'

  return (
    <div className="w-full max-w-2xl pt-2 lg:pt-6">
      <div className="lg:hidden flex items-center justify-center gap-2.5 font-bold tracking-tight text-[22px] mb-6">
        <span className="w-8 h-8 rounded-[10px] grid place-items-center font-extrabold text-sm text-white bg-[#10B981] shadow-[0_4px_10px_-3px_rgba(16,185,129,0.5)]">U</span>
        UGC<b className="text-[#059669] font-extrabold">4you</b>
      </div>

      <div className="text-center mb-6">
        <h1 className="text-[28px] md:text-[34px] font-bold tracking-[-0.03em] leading-[1.1] text-[#0A0F0D]">{title}</h1>
        <button
          onClick={onBackToRole}
          className={`mt-2 inline-flex items-center text-sm font-semibold ${accent.text} hover:underline`}
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Andere rol kiezen
        </button>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-[20px] p-5 md:p-7 shadow-[0_1px_2px_rgba(0,0,0,0.03),0_8px_24px_-12px_rgba(0,0,0,0.06)]">
        {errors.general && (
          <div className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-lg flex gap-2.5 items-start">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{errors.general}</p>
          </div>
        )}

        {/* Google sign-up */}
        <button
          type="button"
          onClick={() => { window.location.href = `${import.meta.env.VITE_API_URL || 'https://ugc4you.nl/api'}/auth/google/${selectedRole}` }}
          className="w-full flex items-center justify-center gap-2.5 px-4 py-3 border border-[#E5E7EB] rounded-[10px] bg-white hover:bg-[#FAFAF7] hover:border-[#1F2937] transition mb-5 text-sm font-semibold text-[#1F2937]"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
            <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84Z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
          </svg>
          Doorgaan met Google
        </button>

        <div className="flex items-center gap-3.5 mb-5 text-[13px] text-[#64748B] font-medium">
          <div className="flex-1 h-px bg-[#E5E7EB]" /> Of vul het formulier in <div className="flex-1 h-px bg-[#E5E7EB]" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {selectedRole === 'creator' && (
            <CreatorFields formData={formData} errors={errors} handleChange={handleChange} accent={accent} />
          )}
          {selectedRole === 'bedrijf' && (
            <BedrijfFields formData={formData} errors={errors} handleChange={handleChange} accent={accent} />
          )}
          {selectedRole === 'agency' && (
            <AgencyFields formData={formData} errors={errors} handleChange={handleChange} accent={accent} />
          )}

          {/* Password fields (common) */}
          <Field label="Wachtwoord" error={errors.password}>
            <InputWithIcon
              icon={<Lock className="w-[18px] h-[18px] text-[#64748B] ml-3.5 flex-shrink-0" />}
              error={errors.password} accent={accent}
            >
              <input
                id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                value={formData.password} onChange={handleChange}
                placeholder="Minimaal 6 tekens"
                className="flex-1 min-w-0 w-full px-3 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8]"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="py-2 px-3.5 text-[#64748B] hover:text-[#0A0F0D]"
                      aria-label={showPassword ? 'Verberg wachtwoord' : 'Toon wachtwoord'}>
                {showPassword ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
              </button>
            </InputWithIcon>
          </Field>

          <Field label="Bevestig wachtwoord" error={errors.confirmPassword}>
            <InputWithIcon
              icon={<Lock className="w-[18px] h-[18px] text-[#64748B] ml-3.5 flex-shrink-0" />}
              error={errors.confirmPassword} accent={accent}
            >
              <input
                id="confirmPassword" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password"
                value={formData.confirmPassword} onChange={handleChange}
                placeholder="Herhaal je wachtwoord"
                className="flex-1 min-w-0 w-full px-3 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8]"
              />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="py-2 px-3.5 text-[#64748B] hover:text-[#0A0F0D]"
                      aria-label={showConfirmPassword ? 'Verberg wachtwoord' : 'Toon wachtwoord'}>
                {showConfirmPassword ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
              </button>
            </InputWithIcon>
          </Field>

          {/* Agree terms */}
          <label className="flex items-start gap-3 py-1 cursor-pointer">
            <input
              type="checkbox"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
              className={`mt-0.5 h-5 w-5 flex-shrink-0 rounded border-gray-300 ${accent.text}`}
              style={{ accentColor: selectedRole === 'creator' ? '#7c3aed' : selectedRole === 'agency' ? '#10B981' : '#3EB489' }}
            />
            <span className="text-xs text-[#64748B] leading-relaxed">
              {selectedRole === 'creator'
                ? <>Ik bevestig dat ik als zelfstandig ondernemer diensten lever aan UGC4you B.V. en ga akkoord met de <Link to="/algemene-voorwaarden" target="_blank" className={`${accent.text} underline font-medium`}>algemene voorwaarden</Link> en de <Link to="/self-billing-overeenkomst" target="_blank" className={`${accent.text} underline font-medium`}>self-billing overeenkomst</Link>.</>
                : <>Ik ga akkoord met de <Link to="/algemene-voorwaarden" target="_blank" className={`${accent.text} underline font-medium`}>algemene voorwaarden</Link> van UGC4you B.V.</>
              }
            </span>
          </label>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !agreeTerms}
            className={`w-full inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-[11px] ${accent.bg} ${accent.hover} text-white text-[15px] font-semibold tracking-tight transition transform hover:-translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-md`}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                Account aanmaken
              </>
            )}
          </button>
        </form>

        <div className="mt-5 text-center text-sm text-[#64748B]">
          Al een account?{' '}
          <Link to="/login" className={`font-semibold ${accent.text} hover:underline`}>Log hier in</Link>
        </div>
      </div>
    </div>
  )
}

// ====== Generic field wrappers ======

const Field = ({ label, error, children, hint, optional }) => (
  <div>
    {label && (
      <label className="block text-[13.5px] font-semibold text-[#1F2937] mb-1.5">
        {label}{optional && <span className="font-normal text-[#94A3B8]"> (optioneel)</span>}
      </label>
    )}
    {children}
    {hint && !error && <p className="mt-1 text-xs text-[#94A3B8]">{hint}</p>}
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
)

const InputWithIcon = ({ icon, error, accent, children }) => (
  <div className={`flex items-center bg-white border rounded-[10px] transition focus-within:ring-4 ${error ? 'border-red-300' : 'border-[#E5E7EB]'} ${accent.ring} focus-within:ring-[#D1FAE5]`}>
    {icon}
    {children}
  </div>
)

const PlainInput = ({ id, name, type = 'text', value, onChange, placeholder, autoComplete, error, accent, ...rest }) => (
  <div className={`flex items-center bg-white border rounded-[10px] transition focus-within:ring-4 ${error ? 'border-red-300' : 'border-[#E5E7EB]'} ${accent.ring} focus-within:ring-[#D1FAE5]`}>
    <input
      id={id} name={name} type={type} value={value} onChange={onChange} placeholder={placeholder} autoComplete={autoComplete}
      className="flex-1 min-w-0 w-full px-3.5 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8]"
      {...rest}
    />
  </div>
)

const PlainSelect = ({ id, name, value, onChange, error, accent, children }) => (
  <div className={`flex items-center bg-white border rounded-[10px] transition focus-within:ring-4 ${error ? 'border-red-300' : 'border-[#E5E7EB]'} ${accent.ring} focus-within:ring-[#D1FAE5]`}>
    <select
      id={id} name={name} value={value} onChange={onChange}
      className="flex-1 min-w-0 w-full px-3.5 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] cursor-pointer"
    >
      {children}
    </select>
  </div>
)

// ====== Role-specific field sets ======

const CreatorFields = ({ formData, errors, handleChange, accent }) => (
  <>
    <SectionHeader n={1} title="Persoonlijke gegevens" desc="Vul je gegevens in zoals ze op je ID staan" accent={accent} />

    <Field label="E-mailadres *" error={errors.email} hint="Hierop ontvang je een verificatiecode na registratie">
      <InputWithIcon icon={<Mail className="w-[18px] h-[18px] text-[#64748B] ml-3.5 flex-shrink-0" />} error={errors.email} accent={accent}>
        <input id="email" name="email" type="email" autoComplete="email" value={formData.email} onChange={handleChange}
               placeholder="jouw.email@voorbeeld.nl"
               className="flex-1 min-w-0 w-full px-3 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8]" />
      </InputWithIcon>
    </Field>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Voornaam *" error={errors.firstName}>
        <InputWithIcon icon={<User className="w-[18px] h-[18px] text-[#64748B] ml-3.5 flex-shrink-0" />} error={errors.firstName} accent={accent}>
          <input id="firstName" name="firstName" type="text" autoComplete="given-name" value={formData.firstName} onChange={handleChange}
                 placeholder="Je voornaam"
                 className="flex-1 min-w-0 w-full px-3 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8]" />
        </InputWithIcon>
      </Field>
      <Field label="Achternaam *" error={errors.lastName}>
        <PlainInput id="lastName" name="lastName" autoComplete="family-name" value={formData.lastName} onChange={handleChange} placeholder="Je achternaam" error={errors.lastName} accent={accent} />
      </Field>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Geboortedatum *" error={errors.dateOfBirth} hint="Je moet 18+ zijn">
        <InputWithIcon icon={<Calendar className="w-[18px] h-[18px] text-[#64748B] ml-3.5 flex-shrink-0" />} error={errors.dateOfBirth} accent={accent}>
          <input id="dateOfBirth" name="dateOfBirth" type="date" autoComplete="bday" value={formData.dateOfBirth} onChange={handleChange}
                 className="flex-1 min-w-0 w-full px-3 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D]" />
        </InputWithIcon>
      </Field>
      <Field label="Telefoonnummer *" error={errors.phoneNumber}>
        <InputWithIcon icon={<Phone className="w-[18px] h-[18px] text-[#64748B] ml-3.5 flex-shrink-0" />} error={errors.phoneNumber} accent={accent}>
          <input id="phoneNumber" name="phoneNumber" type="tel" autoComplete="tel" value={formData.phoneNumber} onChange={handleChange}
                 placeholder="+31 6 12345678"
                 className="flex-1 min-w-0 w-full px-3 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8]" />
        </InputWithIcon>
      </Field>
    </div>

    <SectionHeader n={2} title="Je profiel" desc="Dit zien bedrijven als ze jouw profiel bekijken" accent={accent} />

    <Field label="Bio (vertel iets over jezelf) *" error={errors.bio}>
      <div className={`bg-white border rounded-[10px] transition focus-within:ring-4 ${errors.bio ? 'border-red-300' : 'border-[#E5E7EB]'} ${accent.ring} focus-within:ring-[#D1FAE5]`}>
        <textarea id="bio" name="bio" rows={3} value={formData.bio} onChange={handleChange}
                  maxLength={1000}
                  placeholder="Vertel iets over jezelf en je ervaring met content maken..."
                  className="w-full min-w-0 px-3.5 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8] resize-none rounded-[10px]" />
      </div>
      <div className="mt-1.5 flex justify-end">
        <span className={`text-xs ${(formData.bio?.length || 0) >= 1000 ? 'text-red-500 font-semibold' : 'text-[#94A3B8]'}`}>
          {formData.bio?.length || 0} / 1000 tekens
        </span>
      </div>
      <div className="mt-2 p-3 bg-white rounded-lg border border-[#E5E7EB]">
        <p className={`text-xs ${accent.text} font-semibold mb-1`}>Tip: Een goede bio bevat:</p>
        <ul className="text-xs text-[#64748B] space-y-0.5">
          <li>- Wat voor content je maakt (bijv. lifestyle, beauty, food)</li>
          <li>- Je ervaring of specialisatie</li>
          <li>- Waarom bedrijven jou zouden moeten kiezen</li>
        </ul>
      </div>
    </Field>

    <SectionHeader n={3} title="Fiscale status" desc="Ook zonder KvK kun je als creator werken" accent={accent} />

    <div>
      <label className="block text-[13.5px] font-semibold text-[#1F2937] mb-2">Wat is jouw situatie? *</label>
      <div className="space-y-2">
        {[
          { value: 'particulier', title: 'Particulier (geen KvK)', desc: 'Je hebt geen bedrijf. UGC4you regelt de facturatie. Je geeft je inkomsten op als "overige werkzaamheden" bij de belasting.' },
          { value: 'kor',         title: 'KvK zonder BTW (KOR)', desc: 'Je hebt een KvK-inschrijving maar valt onder de Kleineondernemersregeling (geen BTW).' },
          { value: 'btw_plichtig',title: 'KvK met BTW-nummer', desc: 'Je hebt een KvK-inschrijving en bent BTW-plichtig.' },
        ].map(opt => (
          <label key={opt.value} className={`flex items-start gap-3 p-3 rounded-[10px] border bg-white cursor-pointer transition ${
            formData.btwStatus === opt.value ? `${accent.border} shadow-sm` : 'border-[#E5E7EB] hover:border-gray-300'
          }`}>
            <input type="radio" name="btwStatus" value={opt.value} checked={formData.btwStatus === opt.value} onChange={handleChange}
                   className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ accentColor: '#7c3aed' }} />
            <div>
              <span className="text-sm font-semibold text-[#0A0F0D]">{opt.title}</span>
              <p className="text-xs text-[#64748B] mt-0.5 leading-relaxed">{opt.desc}</p>
            </div>
          </label>
        ))}
      </div>
      {errors.btwStatus && <p className="mt-1 text-xs text-red-600">{errors.btwStatus}</p>}
    </div>

    {(formData.btwStatus === 'kor' || formData.btwStatus === 'btw_plichtig') && (
      <Field label="KVK-nummer *" error={errors.kvkNummerCreator} hint="Je KVK-nummer bestaat uit 8 cijfers">
        <PlainInput id="kvkNummerCreator" name="kvkNummerCreator" type="text" maxLength="8"
          value={formData.kvkNummerCreator || ''}
          onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 8); handleChange({ target: { name: 'kvkNummerCreator', value: v } }) }}
          placeholder="12345678" error={errors.kvkNummerCreator} accent={accent} />
      </Field>
    )}

    {formData.btwStatus === 'btw_plichtig' && (
      <Field label="BTW-nummer *" error={errors.btwNummer} hint="Format: NL + 9 cijfers + B + 2 cijfers">
        <PlainInput id="btwNummer" name="btwNummer" type="text"
          value={formData.btwNummer || ''}
          onChange={(e) => handleChange({ target: { name: 'btwNummer', value: e.target.value.toUpperCase() } })}
          placeholder="NL123456789B01" error={errors.btwNummer} accent={accent} />
      </Field>
    )}

    {formData.btwStatus === 'particulier' && (
      <div className="p-3 bg-white border border-[#E5E7EB] rounded-lg">
        <p className="text-xs text-[#1F2937]"><strong className="text-[#2A7E5F]">Geen KvK nodig!</strong> UGC4you factureert het bedrijf namens jou (self-billing). Je ontvangt een uitbetalingsoverzicht en geeft je inkomsten op als "overige werkzaamheden".</p>
      </div>
    )}
    {formData.btwStatus === 'kor' && (
      <div className="p-3 bg-white border border-[#E5E7EB] rounded-lg">
        <p className="text-xs text-[#1F2937]">Je valt onder de Kleineondernemersregeling. UGC4you factureert namens jou zonder BTW.</p>
      </div>
    )}
    {formData.btwStatus === 'btw_plichtig' && (
      <div className="p-3 bg-white border border-[#E5E7EB] rounded-lg">
        <p className="text-xs text-[#1F2937]">Je bent BTW-plichtig. UGC4you maakt self-billing facturen aan namens jou inclusief 21% BTW.</p>
      </div>
    )}

    <SectionHeader n={4} title="Account beveiliging" desc="Kies een sterk wachtwoord van minimaal 6 tekens" accent={accent} />
  </>
)

const BedrijfFields = ({ formData, errors, handleChange, accent }) => (
  <>
    <Field label="E-mailadres" error={errors.email}>
      <InputWithIcon icon={<Mail className="w-[18px] h-[18px] text-[#64748B] ml-3.5 flex-shrink-0" />} error={errors.email} accent={accent}>
        <input id="email" name="email" type="email" autoComplete="email" value={formData.email} onChange={handleChange}
               placeholder="jouw.email@voorbeeld.nl"
               className="flex-1 min-w-0 w-full px-3 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8]" />
      </InputWithIcon>
    </Field>

    <Field label="Bedrijfsnaam" error={errors.companyName}>
      <InputWithIcon icon={<Building className="w-[18px] h-[18px] text-[#64748B] ml-3.5 flex-shrink-0" />} error={errors.companyName} accent={accent}>
        <input id="companyName" name="companyName" type="text" autoComplete="organization" value={formData.companyName} onChange={handleChange}
               placeholder="Je bedrijfsnaam"
               className="flex-1 min-w-0 w-full px-3 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8]" />
      </InputWithIcon>
    </Field>

    <Field label="Contactpersoon" error={errors.contactPerson}>
      <InputWithIcon icon={<User className="w-[18px] h-[18px] text-[#64748B] ml-3.5 flex-shrink-0" />} error={errors.contactPerson} accent={accent}>
        <input id="contactPerson" name="contactPerson" type="text" autoComplete="name" value={formData.contactPerson} onChange={handleChange}
               placeholder="Je naam"
               className="flex-1 min-w-0 w-full px-3 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8]" />
      </InputWithIcon>
    </Field>

    <Field label="Branche" error={errors.industry}>
      <PlainSelect id="industry" name="industry" value={formData.industry} onChange={handleChange} error={errors.industry} accent={accent}>
        <option value="">Selecteer branche</option>
        <option value="beauty-cosmetics">Beauty & Cosmetica</option>
        <option value="fashion-clothing">Mode & Kleding</option>
        <option value="food-drinks">Eten & Drinken</option>
        <option value="health-fitness">Gezondheid & Fitness</option>
        <option value="technology">Technologie</option>
        <option value="travel-lifestyle">Reizen & Lifestyle</option>
        <option value="home-garden">Wonen & Tuin</option>
        <option value="business-services">Zakelijke Diensten</option>
        <option value="other">Anders</option>
      </PlainSelect>
    </Field>

    <Field label="Bedrijfsgrootte" error={errors.companySize}>
      <PlainSelect id="companySize" name="companySize" value={formData.companySize} onChange={handleChange} error={errors.companySize} accent={accent}>
        <option value="">Selecteer bedrijfsgrootte</option>
        <option value="1-10">1-10 medewerkers</option>
        <option value="11-50">11-50 medewerkers</option>
        <option value="51-200">51-200 medewerkers</option>
        <option value="200+">200+ medewerkers</option>
      </PlainSelect>
    </Field>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="KVK Nummer" error={errors.kvkNummer} optional>
        <PlainInput id="kvkNummer" name="kvkNummer" type="text" maxLength="8" value={formData.kvkNummer}
          onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 8); handleChange({ target: { name: 'kvkNummer', value: v } }) }}
          placeholder="12345678" error={errors.kvkNummer} accent={accent} />
      </Field>
      <Field label="BTW Nummer" error={errors.btwNummer} optional>
        <PlainInput id="btwNummer" name="btwNummer" type="text" value={formData.btwNummer}
          onChange={(e) => handleChange({ target: { name: 'btwNummer', value: e.target.value.toUpperCase() } })}
          placeholder="NL123456789B01" error={errors.btwNummer} accent={accent} />
      </Field>
    </div>
    <p className="text-xs text-[#94A3B8] -mt-2">KVK en BTW nummer zijn nodig voor facturatie. Je kunt dit ook later invullen.</p>
  </>
)

const AgencyFields = ({ formData, errors, handleChange, accent }) => (
  <>
    <Field label="Agencynaam *" error={errors.agencyName}>
      <InputWithIcon icon={<Building className="w-[18px] h-[18px] text-[#64748B] ml-3.5 flex-shrink-0" />} error={errors.agencyName} accent={accent}>
        <input id="agencyName" name="agencyName" type="text" autoComplete="organization" value={formData.agencyName} onChange={handleChange}
               placeholder="Je agencynaam"
               className="flex-1 min-w-0 w-full px-3 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8]" />
      </InputWithIcon>
    </Field>

    <Field label="Contactpersoon *" error={errors.contactPerson}>
      <InputWithIcon icon={<User className="w-[18px] h-[18px] text-[#64748B] ml-3.5 flex-shrink-0" />} error={errors.contactPerson} accent={accent}>
        <input id="contactPerson" name="contactPerson" type="text" autoComplete="name" value={formData.contactPerson} onChange={handleChange}
               placeholder="Je naam"
               className="flex-1 min-w-0 w-full px-3 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8]" />
      </InputWithIcon>
    </Field>

    <Field label="E-mailadres *" error={errors.email}>
      <InputWithIcon icon={<Mail className="w-[18px] h-[18px] text-[#64748B] ml-3.5 flex-shrink-0" />} error={errors.email} accent={accent}>
        <input id="email" name="email" type="email" autoComplete="email" value={formData.email} onChange={handleChange}
               placeholder="jouw.email@voorbeeld.nl"
               className="flex-1 min-w-0 w-full px-3 py-3 bg-transparent border-0 outline-none text-base sm:text-[14.5px] text-[#0A0F0D] placeholder:text-[#94A3B8]" />
      </InputWithIcon>
    </Field>

    <Field label="Agency grootte *" error={errors.agencySize}>
      <PlainSelect id="agencySize" name="agencySize" value={formData.agencySize} onChange={handleChange} error={errors.agencySize} accent={accent}>
        <option value="">Selecteer agency grootte</option>
        <option value="1-5">1-5 medewerkers</option>
        <option value="6-20">6-20 medewerkers</option>
        <option value="21-50">21-50 medewerkers</option>
        <option value="50+">50+ medewerkers</option>
      </PlainSelect>
    </Field>

    <Field label="Branche *" error={errors.industry}>
      <PlainSelect id="industry" name="industry" value={formData.industry} onChange={handleChange} error={errors.industry} accent={accent}>
        <option value="">Selecteer branche</option>
        <option value="e-commerce">E-commerce</option>
        <option value="fashion-mode">Fashion & Mode</option>
        <option value="beauty-cosmetics">Beauty & Cosmetica</option>
        <option value="technology">Technologie</option>
        <option value="health-fitness">Gezondheid & Fitness</option>
        <option value="food-drinks">Eten & Drinken</option>
        <option value="travel">Reizen</option>
        <option value="finance">Finance</option>
        <option value="real-estate">Vastgoed</option>
        <option value="entertainment">Entertainment</option>
        <option value="sports">Sport</option>
        <option value="other">Overig</option>
      </PlainSelect>
    </Field>

    <Field label="Website" optional>
      <PlainInput id="website" name="website" type="url" value={formData.website} onChange={handleChange}
        placeholder="https://www.jouwagency.nl" accent={accent} />
    </Field>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="KVK-nummer *" error={errors.kvkNummer}>
        <PlainInput id="kvkNummer" name="kvkNummer" type="text" maxLength="8" value={formData.kvkNummer}
          onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 8); handleChange({ target: { name: 'kvkNummer', value: v } }) }}
          placeholder="12345678" error={errors.kvkNummer} accent={accent} />
      </Field>
      <Field label="BTW-nummer *" error={errors.btwNummer}>
        <PlainInput id="btwNummer" name="btwNummer" type="text" value={formData.btwNummer}
          onChange={(e) => handleChange({ target: { name: 'btwNummer', value: e.target.value.toUpperCase() } })}
          placeholder="NL123456789B01" error={errors.btwNummer} accent={accent} />
      </Field>
    </div>
  </>
)

const SectionHeader = ({ n, title, desc, accent }) => (
  <div className="flex items-center gap-3 pb-2 border-b border-[#F1F3F5] pt-2">
    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${accent.bg}`}>
      <span className="text-white text-xs font-bold">{n}</span>
    </div>
    <div>
      <h3 className="text-sm font-bold text-[#0A0F0D]">{title}</h3>
      <p className="text-xs text-[#64748B]">{desc}</p>
    </div>
  </div>
)

// ====== Step 3: Verification ======

const VerificationView = ({
  verificationEmail, verificationCode, setVerificationCode, errors, setErrors,
  loading, resendLoading, isPositiveVerification, onSubmit, onResend, onBack,
}) => (
  <div className="w-full max-w-[420px] pt-2 lg:pt-6">
    <div className="lg:hidden flex items-center justify-center gap-2.5 font-bold tracking-tight text-[22px] mb-8">
      <span className="w-8 h-8 rounded-[10px] grid place-items-center font-extrabold text-sm text-white bg-[#10B981] shadow-[0_4px_10px_-3px_rgba(16,185,129,0.5)]">U</span>
      UGC<b className="text-[#059669] font-extrabold">4you</b>
    </div>

    <div className="text-center mb-8">
      <h1 className="text-[28px] sm:text-[32px] font-bold tracking-[-0.03em] leading-[1.1] text-[#0A0F0D]">Verificatiecode</h1>
      <p className="mt-2 text-[#64748B] text-[15px] leading-relaxed">
        We hebben een 6-cijferige code naar <strong className="text-[#0A0F0D]">{verificationEmail}</strong> gestuurd
      </p>
    </div>

    <div className="bg-white border border-[#E5E7EB] rounded-[20px] p-5 sm:p-7 shadow-[0_1px_2px_rgba(0,0,0,0.03),0_8px_24px_-12px_rgba(0,0,0,0.06)]">
      {errors.verification && (
        <div className={`mb-5 p-3.5 border rounded-lg flex gap-2.5 items-start ${
          isPositiveVerification ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
        }`}>
          <AlertCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isPositiveVerification ? 'text-emerald-500' : 'text-red-500'}`} />
          <p className={`text-sm ${isPositiveVerification ? 'text-emerald-800' : 'text-red-800'}`}>{errors.verification}</p>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-5">
        <button type="button" onClick={onBack} className="flex items-center text-sm text-[#64748B] hover:text-[#0A0F0D]">
          <ArrowLeft className="w-4 h-4 mr-2" /> Terug naar formulier
        </button>

        <div>
          <label htmlFor="verificationCode" className="block text-[13.5px] font-semibold text-[#1F2937] mb-3">
            Voer je verificatiecode in
          </label>
          <input
            id="verificationCode" name="verificationCode" type="text" inputMode="numeric"
            maxLength="6" autoComplete="off" autoFocus value={verificationCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '')
              setVerificationCode(value)
              if (errors.verification) setErrors({})
            }}
            placeholder="000000"
            className="w-full min-w-0 px-3 py-4 border border-[#E5E7EB] rounded-[10px] outline-none text-center text-2xl tracking-[0.5em] font-mono text-[#0A0F0D] placeholder:text-[#94A3B8] focus:border-[#10B981] focus:ring-4 focus:ring-[#D1FAE5] transition"
          />
          <p className="mt-2 text-xs text-[#94A3B8] text-center">De code is 10 minuten geldig</p>
        </div>

        <button type="submit" disabled={loading || verificationCode.length !== 6}
                className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-[11px] bg-[#10B981] hover:bg-[#059669] text-white text-[15px] font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ boxShadow: '0 1px 0 rgba(255,255,255,.2) inset, 0 6px 18px -4px rgba(16,185,129,.55)' }}>
          {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Verifiëren en Account Activeren'}
        </button>

        <div className="text-center">
          <button type="button" onClick={onResend} disabled={resendLoading}
                  className="text-sm font-semibold text-[#047857] hover:text-[#065F46] disabled:opacity-50">
            {resendLoading ? 'Versturen...' : 'Code opnieuw versturen'}
          </button>
        </div>
      </form>
    </div>
  </div>
)

export default DarkRegisterPage
